if (!customElements.get('product-form')) {
  class ProductForm extends HTMLElement {
    constructor() {
      super();
      this.isSubmitting = false;
      this.quantityChangeTimeout = null;
      this.cachedCartData = null;
      if (this.hasChildNodes()) this.init();
    }

    init() {
      this.form = this.querySelector('.js-product-form');
      if (this.form) {
        this.form.querySelector('[name="id"]').disabled = false;
        this.cartDrawer = document.querySelector('cart-drawer');
        this.submitBtn = this.querySelector('[name="add"]');

        if (theme.settings.afterAtc !== "no-js") {
          // Add binding for the submit event
          this.addEventListener("submit", this.handleSubmit.bind(this));

          // Add event listener for the 'change' event with handleQuantityChange
          this.form.addEventListener("change", (evt) => {
            if (evt.target.matches('.qty-input__input')) {
              evt.stopPropagation();
              this.handleQuantityChange(evt);
            }
          });
        }
      }
    }

    /**
     * Function to handle quantity changes with debouncing
     */
    handleQuantityChange(evt) {
      const quantityInput = evt.target;
      
      // Clear existing timeout for debouncing
      if (this.quantityChangeTimeout) {
        clearTimeout(this.quantityChangeTimeout);
      }

      // Debounce: wait 300ms before making request
      this.quantityChangeTimeout = setTimeout(() => {
        this.processQuantityChange(quantityInput);
      }, 300);
    }

    /**
     * Process the actual quantity change
     */
    async processQuantityChange(quantityInput) {
      const collectionDataInput = quantityInput.closest('form').querySelector('input[name="CollectionData"]');
      let parentCat = '';
      let childCat = '';
      
      if (collectionDataInput) {
        parentCat = collectionDataInput.getAttribute('data-parenCategory') || '';
        childCat = collectionDataInput.getAttribute('data-childCategory') || '';
      }
     
      const variantId = parseInt(quantityInput.dataset.variantId, 10);
      
      if (!variantId) {
        console.error('Variant ID not found.');
        return;
      }

      const productTags = collectionDataInput 
        ? collectionDataInput.dataset.tags.split(',').map(tag => tag.trim()) 
        : [];    
      
      const newQuantity = parseInt(quantityInput.value, 10);
      const previousValue = quantityInput.dataset.previousValue || quantityInput.dataset.initialValue || '1';
      
      // Default limit if no maxqty_ tag is found
      let quantityLimit = 4;    
      
      // Find the tag that starts with maxqty_
      const maxQtyTag = productTags.find(tag => tag.startsWith("maxqty_"));    
      
      if (maxQtyTag) {
        const limit = parseInt(maxQtyTag.replace("maxqty_", ""), 10);
        if (!isNaN(limit)) {
          quantityLimit = limit;
        }
      }

      if (newQuantity > quantityLimit) {
        this.showErrorMessage(`You can only add up to ${quantityLimit} of the same item.`);
        quantityInput.value = quantityLimit;
        return;
      }

      try {
        // Prepare formData for the cart update request
        const formData = new FormData();
        formData.append('id', variantId);
        formData.append('quantity', newQuantity);
    
        // Include sections data in the formData
        let sections = "cart-icon-bubble";
        if (this.cartDrawer) {
          sections += `,${this.cartDrawer.closest(".shopify-section").id.replace("shopify-section-", "")}`;
        }
        formData.append("sections", sections);
    
        // Make the cart update request
        const changeResponse = await this.fetchWithTimeout(`${theme.routes.cartChange}`, {
          method: 'POST',
          headers: {
            'Accept': 'application/json',
            'X-Requested-With': 'XMLHttpRequest'
          },
          body: formData
        });
    
        if (!changeResponse.ok) {
          const errorData = await changeResponse.json().catch(() => ({}));
          throw new Error(errorData.message || `Server error: ${changeResponse.status}`);
        }

        const updatedCartData = await changeResponse.json();

        // Validate response
        if (!updatedCartData || !Array.isArray(updatedCartData.items)) {
          throw new Error('Invalid response from server');
        }

        // Store current value for future reverts
        quantityInput.dataset.previousValue = newQuantity;

        // Check if the newQuantity is zero AFTER the cart update
        if (newQuantity === 0) {
          // Find the enclosing quantity-input element and hide it
          const quantityInputWrapper = quantityInput.closest('.qty-input');
          if (quantityInputWrapper) {
            quantityInputWrapper.style.display = 'none';
          }

          // Display the primary cart button
          const productForm = quantityInput.closest('product-form');
          if (productForm) {
            const primaryCartButton = productForm.querySelector('.btn--primary');
            if (primaryCartButton) {
              primaryCartButton.style.display = 'block';
            }
          }
        }

        // Update cart UI based on the updated cart data
        ProductForm.updateCartIcon(updatedCartData);
        
        if (newQuantity > 1) {
          // Check if the variantId matches any item in the updatedCartData
          const matchingItem = updatedCartData.items.find(item => item.variant_id === variantId);
      
          if (matchingItem) {
            // Fire analytics asynchronously
            this.fireAnalyticsAsync(matchingItem, parentCat, childCat);
          }
        }
   
        // Additional UI updates as needed
        const quickAddDrawer = this.closest("quick-add-drawer");
        if (quickAddDrawer) quickAddDrawer.addedToCart();
    
        if (this.cartDrawer) {
          this.cartDrawer.renderContents(
            updatedCartData,
            !quickAddDrawer && theme.settings.afterAtc === "drawer"
          );
        } else if (window.location.pathname === theme.routes.cart) {
          const cartItems = document.querySelector("cart-items");
          if (cartItems) {
            if (cartItems.dataset.empty === "true") {
              window.location.reload();
            } else {
              cartItems.refreshCart();
            }
          }
        }

        this.dispatchEvent(
          new CustomEvent("on:line-item:change", {
            bubbles: true,
            detail: {
              variantId: variantId,
              newQuantity: newQuantity,
            },
          })
        );
    
      } catch (error) {
        console.error('Error updating quantity:', error);
        
        // Revert to previous value
        quantityInput.value = previousValue;
        
        // Show user-friendly error
        this.showErrorMessage(
          error.message.includes('Server error') 
            ? 'Unable to update cart. Please try again.' 
            : error.message
        );
      }
    }

    /**
     * Handles submission of the product form - OPTIMIZED VERSION
     * @param {object} evt - Event object.
     */
    async handleSubmit(evt) {
      if (evt.target.id === "product-signup_form") return;

      evt.preventDefault();

      if (this.submitBtn.getAttribute("aria-disabled") === "true") return;
      if (this.isSubmitting) return; // Prevent double submission

      this.isSubmitting = true;

      if (theme.settings.vibrateOnATC && window.navigator.vibrate) {
        window.navigator.vibrate(100);
      }

      this.errorMsg = null;
      this.setErrorMsgState();

      // Disable "Add to Cart" button until submission is complete.
      this.submitBtn.setAttribute("aria-disabled", "true");
      this.submitBtn.classList.add("is-loading");
      
      // Store original button text
      this.originalButtonText = this.submitBtn.textContent;

      // Find the hidden input field with the name 'CollectionData'
      const collectionDataInputSubmit = evt.target.querySelector('input[name="CollectionData"]');
      let parentCat = '';
      let childCat = '';
      let productTags = [];

      if (collectionDataInputSubmit) {
        parentCat = collectionDataInputSubmit.getAttribute('data-parencategory') || '';
        childCat = collectionDataInputSubmit.getAttribute('data-childcategory') || '';
        productTags = collectionDataInputSubmit.getAttribute('data-tags')
          ? collectionDataInputSubmit.getAttribute('data-tags').split(',').map(tag => tag.trim())
          : [];
      }

      const formData = new FormData(this.form);
      const variantId = parseInt(formData.get('id'));
      const requestedQty = parseInt(formData.get('quantity')) || 1;

      let sections = "cart-icon-bubble";
      if (this.cartDrawer) {
        sections += `,${this.cartDrawer.closest(".shopify-section").id.replace("shopify-section-", "")}`;
      }
      formData.append("sections_url", window.location.pathname);
      formData.append("sections", sections);

      const fetchRequestOpts = {
        method: "POST",
        headers: {
          Accept: "application/json",
          "X-Requested-With": "XMLHttpRequest",
        },
        body: formData,
      };

      // Get quantity limit
      let quantityLimit = 4;
      const maxQtyTag = productTags.find(tag => tag.startsWith("maxqty_"));
      if (maxQtyTag) {
        const limit = parseInt(maxQtyTag.replace("maxqty_", ""), 10);
        if (!isNaN(limit)) {
          quantityLimit = limit;
        }
      }

      try {
        // OPTIMIZATION: Parallel fetch of old cart data (optional, for quantity check)
        // We'll do a quick check only if we have cached data, otherwise proceed
        let needsQuantityCheck = false;
        let existingQuantity = 0;

        if (this.cachedCartData && this.cachedCartData.items) {
          const itemInCache = this.cachedCartData.items.find(
            (item) => item.variant_id === variantId
          );
          if (itemInCache) {
            existingQuantity = itemInCache.quantity;
            needsQuantityCheck = true;
          }
        }

        // Quick validation before expensive network call
        if (needsQuantityCheck && existingQuantity >= quantityLimit) {
          this.showErrorMessage(`You can only add up to ${quantityLimit} of the same item.`);
          this.isSubmitting = false;
          this.submitBtn.classList.remove("is-loading");
          this.submitBtn.removeAttribute("aria-disabled");
          return;
        }

        // MAIN API CALL - Add to cart with sections
        const response = await this.fetchWithTimeout(theme.routes.cartAdd, fetchRequestOpts);
        const data = await response.json();

        // Handle errors from Shopify
        const error = typeof data.description === "string" ? data.description : data.message;

        if (data.status) {
          this.setErrorMsgState(error);
          
          // Check if it's a quantity limit error
          if (error && error.toLowerCase().includes('limit')) {
            this.showErrorMessage(`You can only add up to ${quantityLimit} of the same item.`);
          }
          
          this.isSubmitting = false;
          this.submitBtn.classList.remove("is-loading");
          this.submitBtn.removeAttribute("aria-disabled");
          return;
        }

        if (!response.ok) throw new Error(response.status);

        // Fire analytics asynchronously (non-blocking)
        this.fireAnalyticsAsync(data, parentCat, childCat);

        if (theme.settings.afterAtc === "page") {
          // Allow the tick animation to complete
          setTimeout(() => {
            window.location.href = theme.routes.cart;
          }, 300);
        } else {
          // Update cart icon count immediately
          ProductForm.updateCartIcon(data);

          // If item was added from Quick Add drawer, show "Added to cart" message.
          const quickAddDrawer = this.closest("quick-add-drawer");
          if (quickAddDrawer) quickAddDrawer.addedToCart();

          // Update cart drawer contents immediately (removed setTimeout)
          if (this.cartDrawer) {
            this.cartDrawer.renderContents(
              data,
              !quickAddDrawer && theme.settings.afterAtc === "drawer"
            );
          } else if (window.location.pathname === theme.routes.cart) {
            const cartItems = document.querySelector("cart-items");
            if (cartItems) {
              if (cartItems.dataset.empty === "true") {
                window.location.reload();
              } else {
                cartItems.refreshCart();
              }
            }
          }

          // Update quantity input UI
          this.updateQuantityInputUI(data, variantId, quantityLimit);
        }

        // Fetch updated cart data in background and cache it
        this.updateCartCache();

        // Update sticky PDP if function exists
        if (typeof window.stickyPDPupdate === 'function' && window.location.pathname.includes('products')) {
          this.updateStickyPDPAsync(data);
        }

        // Dispatch appropriate event
        this.dispatchCartEvent(data, variantId);

      } catch (error) {
        console.error('Add to cart error:', error);
        this.setErrorMsgState('Unable to add to cart. Please try again.');
        this.showErrorMessage('Unable to add to cart. Please try again.');
        
        this.dispatchEvent(
          new CustomEvent("on:cart:error", {
            bubbles: true,
            detail: {
              error: error.message,
            },
          })
        );
      } finally {
        this.isSubmitting = false;
        this.submitBtn.classList.add("is-success");
        this.submitBtn.removeAttribute("aria-disabled");
        
        setTimeout(() => {
          this.submitBtn.classList.remove("is-loading");
          this.submitBtn.classList.remove("is-success");
        }, 500);
      }
    }

    /**
     * Update quantity input UI after adding to cart
     */
    updateQuantityInputUI(data, variantId, quantityLimit) {
      // Check if a div for the quantity input already exists for the selected variant.
      let quantityDiv = this.querySelector(
        '.qty-input[data-variant-id="' + data.variant_id + '"]'
      );

      if (!quantityDiv) {
        // If the div doesn't exist, create a new one.
        const newQuantityDiv = document.createElement("quantity-input");
        newQuantityDiv.innerHTML =
          '<div class="qty-input inline-flex items-center w-full variant appending_here" data-variant-id="' +
          data.variant_id +
          '">' +
          '<button type="button" class="qty-input__btn btn btn--minus no-js-hidden" name="minus">' +
          '<span class="visually-hidden">-</span>' +
          "</button>" +
          '<input max="' + quantityLimit + '" type="number" class="qty-input__input input" id="quantity-' +
          data.variant_id +
          '" name="updates[]" min="0" value="' + data.quantity + '" data-index="1" data-initial-value="' + 
          data.quantity + '" data-previous-value="' + data.quantity + '" aria-label="Quantity" data-variant-id="' + 
          data.variant_id + '">' +
          '<button type="button" class="qty-input__btn btn btn--plus no-js-hidden" name="plus">' +
          '<span class="visually-hidden">+</span>' +
          "</button>" +
          "</div>";

        // Append the new div to the form.
        let button = this.querySelector('.btn--primary');
        if (button) {
          button.insertAdjacentElement('afterend', newQuantityDiv);
        }
      } else {
        const inputField = quantityDiv.querySelector('.qty-input__input');
        if (inputField) {
          inputField.value = data.quantity;
          inputField.dataset.previousValue = data.quantity;
        }
      }

      // Hide the "Add to Cart" button when the quantity div is created.
      const addToCartButton = this.querySelector('.btn--primary');
      if (addToCartButton) {
        addToCartButton.style.display = "none";
      }
      
      // Show the div for the quantity input.
      if (quantityDiv) {
        quantityDiv.style.display = "block";
      }
    }

    /**
     * Dispatch appropriate cart event
     */
    dispatchCartEvent(data, variantId) {
      // Check if item already existed in cart (using cached data)
      let itemExisted = false;
      let oldQuantity = 0;

      if (this.cachedCartData && this.cachedCartData.items) {
        const itemInOldCart = this.cachedCartData.items.find(
          (item) => item.variant_id === data.variant_id
        );
        if (itemInOldCart) {
          itemExisted = true;
          oldQuantity = itemInOldCart.quantity;
        }
      }

      if (itemExisted) {
        this.dispatchEvent(
          new CustomEvent("on:line-item:change", {
            bubbles: true,
            detail: {
              variantId: data.variant_id,
              oldQuantity: oldQuantity,
              newQuantity: data.quantity,
            },
          })
        );
      } else {
        this.dispatchEvent(
          new CustomEvent("on:cart:add", {
            bubbles: true,
            detail: {
              variantId: data.variant_id,
            },
          })
        );
      }
    }

    /**
     * Update cart cache in background
     */
    async updateCartCache() {
      try {
        const cartResponse = await fetch(`${theme.routes.cart}.js`);
        if (cartResponse.ok) {
          this.cachedCartData = await cartResponse.json();
        }
      } catch (error) {
        console.error('Failed to update cart cache:', error);
      }
    }

    /**
     * Update sticky PDP asynchronously
     */
    async updateStickyPDPAsync(data) {
      try {
        const cartResponse = await fetch(`${theme.routes.cart}.js`);
        if (cartResponse.ok) {
          const cartData = await cartResponse.json();
          if (typeof window.stickyPDPupdate === 'function') {
            window.stickyPDPupdate(cartData.total_price);
          }
        }
      } catch (error) {
        console.error('Failed to update sticky PDP:', error);
      }
    }

    /**
     * Fire analytics asynchronously without blocking
     */
    fireAnalyticsAsync(data, parentCat, childCat) {
      // Use requestIdleCallback if available, otherwise setTimeout
      const fireAnalytics = () => {
        try {
          const eventData = createSmartechEventData(data, parentCat, childCat);
          smartech('dispatch', 'add to cart custom', eventData);
          createGAEventData(data, parentCat, childCat);
        } catch (error) {
          console.error('Analytics error:', error);
          // Don't let analytics failures affect the cart operation
        }
      };

      if ('requestIdleCallback' in window) {
        requestIdleCallback(fireAnalytics);
      } else {
        setTimeout(fireAnalytics, 0);
      }
    }

    /**
     * Fetch with timeout to prevent hanging requests
     */
    async fetchWithTimeout(url, options, timeout = 10000) {
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), timeout);
      
      try {
        const response = await fetch(url, {
          ...options,
          signal: controller.signal
        });
        clearTimeout(timeoutId);
        return response;
      } catch (error) {
        clearTimeout(timeoutId);
        if (error.name === 'AbortError') {
          throw new Error('Request timed out. Please check your connection.');
        }
        throw error;
      }
    }

    /**
     * Show error message to user
     */
    showErrorMessage(message) {
      // Remove existing error if present
      const existingError = this.querySelector('.cart-error-message');
      if (existingError) existingError.remove();
      
      // For simple alerts, use alert for now (can be replaced with toast notifications)
      alert(message);
    }

    /**
     * Updates the cart icon count in the header.
     * @param {object} response - Response JSON.
     */
    static updateCartIcon(response) {
      const cartIconBubble = document.getElementById("cart-icon-bubble");
      if (cartIconBubble) {
        if (response.sections && response.sections["cart-icon-bubble"]) {
          cartIconBubble.innerHTML = response.sections["cart-icon-bubble"];
        }
      }
    }

    /**
     * Shows/hides an error message.
     * @param {string} [error=false] - Error to show a message for.
     */
    setErrorMsgState(error = false) {
      this.errorMsg = this.errorMsg || this.querySelector(".js-form-error");
      if (!this.errorMsg) return;

      this.errorMsg.hidden = !error;
      if (error) this.errorMsg.textContent = error;
    }
  }

  function createSmartechEventData(productData, parentcategory, subcategory) {
    const currentDate = new Date();
    const formattedDate = `${currentDate.getDate()}-${currentDate.getMonth() + 1}-${currentDate.getFullYear()}`;
    
    const eventData = {
      'prid': parseInt(productData.product_id, 10),
      'name': productData.product_title,
      'brand': productData.vendor,
      'activity_date': formattedDate,
      'variant': productData.variant_title,
      'prqt': 1,
      'price': parseFloat(productData.price / 100),
      'currency': 'INR',
      'image': productData.image,
      'produrl': 'https://plumgoodness.com/products/' + productData.handle,
      'refrer url': 'https://plumgoodness.com/products/' + productData.handle,
      'producttype': productData.product_type,
      'pagetype': window.location.href,
      'category': parentcategory,
      'subcategory': subcategory
    };

    return eventData;
  } 

  function createGAEventData(productData, parentcategory, subcategory) {
    dataLayer.push({ ecommerce: null });
    dataLayer.push({
      event: "GA4_add_to_cart",
      ecommerce: {
        items: [
          {
            'item_id': parseInt(productData.product_id, 10),
            'item_name': productData.product_title,
            'item_brand': productData.vendor,
            'item_variant': productData.variant_title,
            'quantity': 1,
            'price': parseFloat(productData.price / 100),
            'currency': 'INR',
            'image': productData.image,
            'item_category': parentcategory,
            'item_category2': subcategory
          }
        ]
      }
    });
  }

  customElements.define("product-form", ProductForm);
}